// src/pages/ForgotPasswordEducator.js
import React, { useState } from "react";
import { Link } from "react-router-dom";

/** Normalize the base URL so we never double-slash */
function getApiBase() {
  const raw = (process.env.REACT_APP_API_BASE_URL || "").trim();
  return raw.replace(/\/+$/, "");
}

/** Read JSON if possible; otherwise give us the raw text. */
async function parseResponse(res) {
  const text = await res.text().catch(() => "");
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }
  return { ok: res.ok, status: res.status, data, text };
}

/** Turn HTML/404 pages into a clean message */
function humanizeError({ status, data, text }) {
  const serverMsg =
    (data && (data.detail || data.error || data.message)) || "";
  const body = (serverMsg || text || "").trim();

  const looksHtml =
    /^<!doctype html/i.test(body) ||
    /^<html/i.test(body) ||
    /<head>|<body>|<\/html>/i.test(body);

  if (looksHtml && status === 404) {
    return "That endpoint was not found on the server (404).";
  }
  if (looksHtml) {
    return `Server returned an HTML error page (HTTP ${status}).`;
  }
  if (!body) {
    return `Request failed (HTTP ${status}).`;
  }
  return body;
}

async function doGet(apiBase, path) {
  try {
    const res = await fetch(`${apiBase}${path}`, {
      method: "GET",
      credentials: "omit",
    });
    return await parseResponse(res);
  } catch {
    return { ok: false, status: 0, data: null, text: "Network error." };
  }
}

async function doPost(apiBase, path, body) {
  try {
    const res = await fetch(`${apiBase}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "omit",
      body: JSON.stringify(body),
    });
    return await parseResponse(res);
  } catch {
    return { ok: false, status: 0, data: null, text: "Network error." };
  }
}

export default function ForgotPasswordEducator() {
  const apiBase = getApiBase();

  const [email, setEmail] = useState("");
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [requiresRequest, setRequiresRequest] = useState(false);

  function validateReset() {
    if (!email) return "Please enter your email.";
    if (!answer) return "Please enter your security answer.";
    if (!pw1 || !pw2) return "Please enter and confirm your new password.";
    if (pw1 !== pw2) return "Passwords do not match.";
    if (pw1.length < 8) return "Password must be at least 8 characters.";
    return "";
  }

  async function getQuestion() {
    if (!email) {
      setMessage("Please enter your email first.");
      return;
    }
    setBusy(true);
    setMessage("");
    setQuestion("");
    setRequiresRequest(false);

    const emailQ = encodeURIComponent(email.trim().toLowerCase());

    try {
      const res = await doGet(apiBase, `/api/auth/security-question/?email=${emailQ}`);
      if (!res.ok) {
        setMessage(humanizeError(res));
        return;
      }

      const q = (res.data && res.data.question) || "";
      if (!q) {
        setRequiresRequest(true);
        setMessage(
          "No security question is set for this account. You can submit a reset request below."
        );
        return;
      }
      setQuestion(String(q));
      setMessage("Security question loaded. Please enter your answer and new password.");
    } catch (e) {
      setMessage("Network error while loading your security question.");
    } finally {
      setBusy(false);
    }
  }

  async function onReset(e) {
    e.preventDefault();
    if (busy) return;

    const v = validateReset();
    if (v) {
      setMessage(v);
      return;
    }

    setBusy(true);
    setMessage("");

    const payload = {
      email: email.trim().toLowerCase(),
      answer: answer || "",
      new_password: pw1,
    };

    try {
      const res = await doPost(apiBase, `/api/auth/security-question/verify-reset/`, payload);

      if (!res.ok) {
        setMessage(humanizeError(res));
        return;
      }

      setMessage("Password updated! You can sign in with your new password.");
      setAnswer("");
      setPw1("");
      setPw2("");
    } catch {
      setMessage("Network error while resetting your password.");
    } finally {
      setBusy(false);
    }
  }

  async function onRequestReset(e) {
    e.preventDefault();
    if (busy) return;
    if (!email) {
      setMessage("Please enter your email first.");
      return;
    }
    if (!firstName.trim() || !lastName.trim()) {
      setMessage("Please enter your first and last name.");
      return;
    }

    setBusy(true);
    setMessage("");

    const body = {
      role: "educator",
      email: email.trim().toLowerCase(),
      first_name: firstName.trim(),
      last_name: lastName.trim(),
    };

    try {
      const res = await doPost(apiBase, `/api/auth/password-reset/educator-request/`, body);
      if (!res.ok) {
        setMessage(humanizeError(res));
        return;
      }
      setMessage(
        "Request submitted. An administrator will review and help complete your reset."
      );
    } catch {
      setMessage("Network error while sending the request.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={styles.wrap}>
      <div style={styles.card}>
        <img
          src="/icons/Propel_schools_logo.png"
          alt="Propel Schools"
          width={48}
          height={48}
          style={{ justifySelf: "center" }}
        />
        <h1 style={styles.h1}>Educator Password Reset</h1>
        <p style={styles.p}>
          Enter your school email to load your security question. After answering it, set
          a new password.
        </p>

        {/* Step 1: Email + Get Question */}
        <div style={{ display: "grid", gap: 10 }}>
          <label style={styles.label} htmlFor="email">
            Email
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={styles.input}
            disabled={busy}
            placeholder="first.last@propelschools.org"
            required
          />

          {!question && !requiresRequest && (
            <button onClick={getQuestion} style={styles.btn} disabled={busy}>
              {busy ? "Checking…" : "Get Security Question"}
            </button>
          )}
        </div>

        {/* Step 2: Show question → answer + new password */}
        {question && (
          <form onSubmit={onReset} style={{ display: "grid", gap: 10 }}>
            <div style={styles.qBox}>
              <span style={styles.qLabel}>Security Question</span>
              <div style={styles.qText}>{question}</div>
            </div>

            <label style={styles.label} htmlFor="answer">
              Answer
            </label>
            <input
              id="answer"
              type="text"
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              style={styles.input}
              disabled={busy}
              required
            />

            <label style={styles.label} htmlFor="pw1">
              New password
            </label>
            <input
              id="pw1"
              type="password"
              autoComplete="new-password"
              value={pw1}
              onChange={(e) => setPw1(e.target.value)}
              style={styles.input}
              disabled={busy}
              required
            />

            <label style={styles.label} htmlFor="pw2">
              Confirm password
            </label>
            <input
              id="pw2"
              type="password"
              autoComplete="new-password"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              style={styles.input}
              disabled={busy}
              required
            />

            <div style={{ display: "flex", gap: 8 }}>
              <button type="submit" style={styles.btn} disabled={busy}>
                {busy ? "Resetting…" : "Reset Password"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setQuestion("");
                  setAnswer("");
                  setMessage("");
                }}
                disabled={busy}
                style={styles.btnOutline}
              >
                Start over
              </button>
            </div>
          </form>
        )}

        {/* Step 2b: If no question on file, allow request */}
        {requiresRequest && (
          <form onSubmit={onRequestReset} style={{ display: "grid", gap: 10 }}>
            <div style={styles.qBox}>
              <span style={styles.qLabel}>No security question on file</span>
              <div style={styles.qTextSmall}>
                We can submit a reset request with your name and email so an administrator
                can help complete the reset.
              </div>
            </div>

            <label style={styles.label} htmlFor="firstName">
              First name
            </label>
            <input
              id="firstName"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              style={styles.input}
              disabled={busy}
              required
            />

            <label style={styles.label} htmlFor="lastName">
              Last name
            </label>
            <input
              id="lastName"
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              style={styles.input}
              disabled={busy}
              required
            />

            <button type="submit" style={styles.btn} disabled={busy}>
              {busy ? "Submitting…" : "Submit Reset Request"}
            </button>
          </form>
        )}

        {/* Status / error message */}
        {message && <div role="status" style={styles.msg}>{message}</div>}

        <div style={styles.footer}>
          <Link to="/recover" style={styles.link}>
            Back: choose Educator or Scholar
          </Link>
          <span style={{ margin: "0 6px" }}>·</span>
          <Link to="/welcome" style={styles.link}>
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}

const styles = {
  wrap: {
    minHeight: "100vh",
    display: "grid",
    placeItems: "center",
    background: "#F7F9FC",
    padding: 24,
  },
  card: {
    width: 460,
    maxWidth: "92vw",
    background: "#fff",
    padding: 24,
    borderRadius: 16,
    boxShadow: "0 10px 24px rgba(0,0,0,0.08)",
    display: "grid",
    gap: 12,
  },
  h1: { margin: 0, fontSize: 22, color: "#003e73", textAlign: "center" },
  p: { margin: "0 0 8px 0", color: "#042d60", textAlign: "center" },
  label: { fontWeight: 700, color: "#042d60" },
  input: {
    width: "100%",
    height: 44,
    borderRadius: 12,
    border: "1px solid rgba(0,0,0,0.12)",
    padding: "0 12px",
  },
  qBox: {
    padding: 12,
    borderRadius: 12,
    background: "#f7f9fc",
    border: "1px solid rgba(0,0,0,0.08)",
  },
  qLabel: { fontSize: 12, color: "#335", fontWeight: 800, textTransform: "uppercase" },
  qText: { marginTop: 6, fontSize: 16, fontWeight: 700, color: "#003e73" },
  qTextSmall: { marginTop: 6, fontSize: 14, color: "#003e73" },
  btn: {
    height: 48,
    border: "none",
    borderRadius: 999,
    background: "#003e73",
    color: "#fff",
    fontWeight: 900,
    cursor: "pointer",
    padding: "0 16px",
  },
  btnOutline: {
    height: 48,
    borderRadius: 999,
    border: "2px solid #003e73",
    background: "#fff",
    color: "#003e73",
    fontWeight: 900,
    cursor: "pointer",
    padding: "0 16px",
  },
  msg: { marginTop: 8, fontSize: 14, color: "#042d60" },
  footer: { marginTop: 8, textAlign: "center" },
  link: { color: "#003e73", textDecoration: "underline", fontWeight: 800 },
};
